class HarvestTimeAPI {
  constructor(getToken = null) {
    if (process.env.NODE_ENV === 'production') {
      this.apiUrl = '/api';
    } else if (process.env.REACT_APP_API_URL) {
      this.apiUrl = `${process.env.REACT_APP_API_URL}/api`;
    } else {
      // Use current hostname for local network access
      const hostname = window.location.hostname;
      this.apiUrl = `http://${hostname}:3151/api`;
    }
    this.getToken = getToken;
  }

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const url = `${this.apiUrl}${endpoint}`;
    const maxRetries = 4;
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers
        });

        if (!response.ok) {
          // Retry on 503 (Service Unavailable) - often cold start timeouts
          // Retry on 429 (Too Many Requests) - rate limited
          if ((response.status === 503 || response.status === 429) && attempt < maxRetries) {
            const retryAfter = response.headers.get('Retry-After');
            // Longer backoff for 429: 2s, 4s, 8s, 16s
            const baseDelay = response.status === 429
              ? Math.pow(2, attempt + 1) * 1000
              : Math.pow(2, attempt) * 1000;
            const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : baseDelay;
            console.log(`API request got ${response.status}, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Request failed: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        lastError = error;
        // Retry on network errors too
        if (attempt < maxRetries && error.name === 'TypeError') {
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`API request failed with network error, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }

    console.error(`API request failed: ${endpoint}`, lastError);
    throw lastError;
  }

  // Health check
  async checkHealth() {
    return this.request('/health');
  }

  // Cross-links
  async getLinks() {
    return this.request('/links');
  }

  // Configuration
  async getAreas() {
    return this.request('/config/areas');
  }

  async getDevices() {
    return this.request('/config/devices');
  }

  async getBuilds() {
    return this.request('/config/builds');
  }

  async getRecentBuilds(buildConfigId) {
    return this.request(`/config/builds/${encodeURIComponent(buildConfigId)}/recent`);
  }

  // Helper to append date range params (days or custom startDate/endDate)
  _appendDateParams(params, days, startDate, endDate) {
    if (startDate) {
      params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
    } else if (days >= 0) {
      params.set('days', String(days));
    }
  }

  // Heatmaps
  async getHeatmapAreas(days = 7, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/heatmaps/areas?${params}`);
  }

  async getHeatmapFilters(days = 7, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/heatmaps/filters?${params}`);
  }

  async getHeatmapAreaFiles(area) {
    return this.request(`/heatmaps/areas/${encodeURIComponent(area)}/files`);
  }

  async getHeatmapAreaData(area, limit = 5, days = 7, filters = {}, metadataOnly = false, startDate = null, endDate = null) {
    const params = new URLSearchParams({ limit: String(limit) });
    this._appendDateParams(params, days, startDate, endDate);
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    if (metadataOnly) params.set('metadataOnly', 'true');
    return this.request(`/heatmaps/areas/${encodeURIComponent(area)}/data?${params}`);
  }

  async getHeatmapFile(fileName) {
    return this.request(`/heatmaps/file/${encodeURIComponent(fileName)}`);
  }

  async getHeatmapMapConfig(zoneName) {
    return this.request(`/heatmaps/maps/${encodeURIComponent(zoneName)}`);
  }

  async getHeatmapMapImage(zoneName) {
    const token = await this.getToken();
    const response = await fetch(`${this.apiUrl}/heatmaps/maps/${encodeURIComponent(zoneName)}/image`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch map image: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async getHeatmapMaps() {
    return this.request('/heatmaps/maps');
  }

  // BigQuery Heatmaps
  async getBigQueryHeatmapAreas(days = 7, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/heatmaps/bigquery/areas?${params}`);
  }

  async getBigQueryHeatmapData(area, days = 7, filters = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    if (filters.tests?.length) params.set('tests', filters.tests.join(','));
    return this.request(`/heatmaps/bigquery/areas/${encodeURIComponent(area)}/data?${params}`);
  }

  async getBigQueryHeatmapFilters(area, days = 7, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/heatmaps/bigquery/areas/${encodeURIComponent(area)}/filters?${params}`);
  }

  async getBigQueryHeatmapGlobalFilters(days = 7, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/heatmaps/bigquery/filters?${params}`);
  }

  /**
   * Get available CLs for an area/branch combination (for compare mode)
   * @param {string} area - Zone name
   * @param {number} days - Days to look back (default 7)
   * @param {string} branch - Branch to filter by (optional)
   * @returns {Promise<Object>} Object with cls array
   */
  async getBigQueryHeatmapCLsForBranch(area, days = 7, branch = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (branch) params.append('branch', branch);
    return this.request(`/heatmaps/bigquery/areas/${encodeURIComponent(area)}/cls?${params}`);
  }

  /**
   * Get heatmap data with LOD (Level of Detail) aggregation
   * @param {string} area - Zone name
   * @param {number} days - Days to look back
   * @param {Object} filters - Filter options (devices, qualities, branches, cls, tests)
   * @param {Object} lodOptions - LOD configuration
   * @param {number|null} lodOptions.cellSize - Grid cell size (100, 25, 5) or null for full resolution
   * @param {number} lodOptions.runLimit - Max runs to include (default 20)
   * @param {Object} lodOptions.viewport - Viewport bounds for full resolution queries
   */
  async getBigQueryHeatmapDataLOD(area, days = 7, filters = {}, lodOptions = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);

    // LOD options
    if (lodOptions.cellSize != null) params.set('cellSize', String(lodOptions.cellSize));
    if (lodOptions.runLimit) params.set('runLimit', String(lodOptions.runLimit));

    // Viewport bounds (for full resolution or partial loads)
    if (lodOptions.viewport) {
      if (lodOptions.viewport.minX != null) params.set('viewMinX', String(lodOptions.viewport.minX));
      if (lodOptions.viewport.maxX != null) params.set('viewMaxX', String(lodOptions.viewport.maxX));
      if (lodOptions.viewport.minZ != null) params.set('viewMinZ', String(lodOptions.viewport.minZ));
      if (lodOptions.viewport.maxZ != null) params.set('viewMaxZ', String(lodOptions.viewport.maxZ));
    }

    // Standard filters
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    if (filters.tests?.length) params.set('tests', filters.tests.join(','));

    return this.request(`/heatmaps/bigquery/areas/${encodeURIComponent(area)}/lod?${params}`);
  }

  async getBigQueryHeatmapRuns(area, days = 7, filters = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    if (filters.tests?.length) params.set('tests', filters.tests.join(','));
    return this.request(`/heatmaps/bigquery/areas/${encodeURIComponent(area)}/runs?${params}`);
  }

  // Performance Analysis
  async getPerformanceSummary(days = 5, branch = null, filters = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (branch) params.append('branch', branch);
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    return this.request(`/heatmaps/bigquery/performance/summary?${params}`);
  }

  async getAreaPerformance(area, days = 5, branch = null, filters = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (branch) params.append('branch', branch);
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    return this.request(`/heatmaps/bigquery/areas/${encodeURIComponent(area)}/performance?${params}`);
  }

  async getAreaDeviceTrend(area, device, quality, days = 5, branch = null, startDate = null, endDate = null) {
    const params = new URLSearchParams({ device, quality });
    this._appendDateParams(params, days, startDate, endDate);
    if (branch) params.append('branch', branch);
    return this.request(`/heatmaps/bigquery/areas/${encodeURIComponent(area)}/trend?${params}`);
  }

  // Performance Trends (captures table)
  async getPerformanceFilters(days = 30, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/performance/filters?${params}`);
  }

  async getPerformanceChangelistDates(days = 30, filters = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    if (filters.tests?.length) params.set('tests', filters.tests.join(','));
    return this.request(`/performance/changelist-dates?${params}`);
  }

  async getPerformanceTrends(days = 30, filters = {}, metric = 'fps', startDate = null, endDate = null) {
    const params = new URLSearchParams({ metric });
    this._appendDateParams(params, days, startDate, endDate);
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    if (filters.tests?.length) params.set('tests', filters.tests.join(','));
    return this.request(`/performance/trends?${params}`);
  }

  async getDeviceComparison(days = 30, filters = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    if (filters.tests?.length) params.set('tests', filters.tests.join(','));
    return this.request(`/performance/device-comparison?${params}`);
  }

  // Ability Performance
  async getAbilityTests(days = 30, filters = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    return this.request(`/performance/ability-tests?${params}`);
  }

  async getAbilityPerformance(testName, days = 30, filters = {}, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.branches?.length) params.set('branches', filters.branches.join(','));
    if (filters.cls?.length) params.set('cls', filters.cls.join(','));
    return this.request(`/performance/ability/${encodeURIComponent(testName)}?${params}`);
  }

  // Quest Performance
  async getQuestPerformanceQuests(days = 30, branch = 'current_dev') {
    const params = new URLSearchParams({ days: String(days), branch });
    return this.request(`/quest-performance/quests?${params}`);
  }

  async getQuestPerformanceZones(questSetup, days = 30, branch = 'current_dev') {
    const params = new URLSearchParams({ days: String(days), branch });
    return this.request(`/quest-performance/quests/${encodeURIComponent(questSetup)}/zones?${params}`);
  }

  async getQuestPerformanceHeatmap(questSetup, zoneName, days = 30, filters = {}, branch = 'current_dev') {
    const params = new URLSearchParams({ days: String(days), branch });
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    if (filters.runLimit) params.set('runLimit', String(filters.runLimit));
    return this.request(`/quest-performance/quests/${encodeURIComponent(questSetup)}/zones/${encodeURIComponent(zoneName)}/heatmap?${params}`);
  }

  async getQuestPerformanceTrend(questSetup, days = 30, filters = {}, branch = 'current_dev') {
    const params = new URLSearchParams({ days: String(days), branch });
    if (filters.devices?.length) params.set('devices', filters.devices.join(','));
    if (filters.qualities?.length) params.set('qualities', filters.qualities.join(','));
    return this.request(`/quest-performance/quests/${encodeURIComponent(questSetup)}/trend?${params}`);
  }

  async getQuestPerformanceFilters(days = 30, branch = 'current_dev') {
    const params = new URLSearchParams({ days: String(days), branch });
    return this.request(`/quest-performance/filters?${params}`);
  }

  // Quest Health
  async getQuestHealthQuests(days = 30, branch = 'current_dev') {
    const params = new URLSearchParams({ days: String(days), branch });
    return this.request(`/quest-health/quests?${params}`);
  }

  async getQuestHealthRuns(questName, days = 30, branch = 'current_dev', altNames = []) {
    const params = new URLSearchParams({ days: String(days), branch });
    if (altNames.length > 0) params.set('altNames', altNames.join(','));
    return this.request(`/quest-health/quests/${encodeURIComponent(questName)}/runs?${params}`);
  }

  async getQuestHealthInvestigations(questName, altNames = []) {
    const params = new URLSearchParams();
    if (altNames.length > 0) params.set('altNames', altNames.join(','));
    const qs = params.toString();
    return this.request(`/quest-health/quests/${encodeURIComponent(questName)}/investigations${qs ? '?' + qs : ''}`);
  }

  async getQuestHealthTrend(days = 30, branch = 'current_dev') {
    const params = new URLSearchParams({ days: String(days), branch });
    return this.request(`/quest-health/trend?${params}`);
  }

  // Asset Budgets
  async getAssetBudgetSummary(days = 7, limit = 20, category = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    if (limit != null) params.set('limit', String(limit));
    if (category) params.append('category', category);
    return this.request(`/asset-budgets/summary?${params}`);
  }

  async getAssetBudgetTrends(days = 30, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/asset-budgets/trends?${params}`);
  }

  async getAssetBudgetSnapshots(days = 7, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/asset-budgets/snapshots?${params}`);
  }

  async deleteAssetBudgetSnapshot(id) {
    return this.request(`/asset-budgets/snapshots/${id}`, {
      method: 'DELETE'
    });
  }

  async getAssetBudgetStatus() {
    return this.request('/asset-budgets/status');
  }

  async syncAssetBudgets() {
    return this.request('/asset-budgets/sync', {
      method: 'POST'
    });
  }

  async getAssetBudgetSettings() {
    return this.request('/asset-budgets/settings');
  }

  async updateAssetBudgetSettings(settings) {
    return this.request('/asset-budgets/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  // STF Scan
  async getSTFScanZones(days = 30, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/stf-scan/zones?${params}`);
  }

  async getSTFScanHistory(zone, days = 90, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    this._appendDateParams(params, days, startDate, endDate);
    return this.request(`/stf-scan/zones/${encodeURIComponent(zone)}/history?${params}`);
  }

  async getSTFScanPoints(zone, testRunId) {
    const params = new URLSearchParams({ testRunId });
    return this.request(`/stf-scan/zones/${encodeURIComponent(zone)}/points?${params}`);
  }

  async getSTFScanPointsAggregated(zone, testRunIds) {
    const params = new URLSearchParams({ testRunIds: testRunIds.join(',') });
    return this.request(`/stf-scan/zones/${encodeURIComponent(zone)}/points/aggregate?${params}`);
  }

  async getSTFScanReports(zone) {
    return this.request(`/stf-scan/zones/${encodeURIComponent(zone)}/reports`);
  }

  async getSTFTrendBaselines(zone, currentTestRunId) {
    return this.request(`/stf-scan/zones/${encodeURIComponent(zone)}/trend-baselines?currentTestRunId=${encodeURIComponent(currentTestRunId)}`);
  }

  getSTFScanReportUrl(reportPath) {
    return `${this.apiUrl}/stf-scan/report?path=${encodeURIComponent(reportPath)}`;
  }

  // Evaluators
  async getEvaluators() {
    return this.request('/evaluators');
  }

  async getEvaluator(id) {
    return this.request(`/evaluators/${encodeURIComponent(id)}`);
  }

  async createEvaluator(evaluator) {
    return this.request('/evaluators', {
      method: 'POST',
      body: JSON.stringify(evaluator)
    });
  }

  async updateEvaluator(id, updates) {
    return this.request(`/evaluators/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async deleteEvaluator(id) {
    return this.request(`/evaluators/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

}

export default HarvestTimeAPI;
